import { useId, type CSSProperties } from 'react';
import { palette } from '../tokens.ts';

export interface CoastProps {
  className?: string;
  style?: CSSProperties;
}

/**
 * A stretch of hand-tinted coast: blurred watercolour wash, an inked shoreline,
 * hachures for the hills, soundings and a couple of sealing-wax reefs.
 */
export function Coast({ className, style }: CoastProps) {
  const id = useId().replace(/:/g, '');
  const shore =
    'M0 120 C40 112 60 132 96 122 C130 112 140 86 176 90 C210 94 222 70 250 58 C280 46 300 58 330 40 C356 24 380 30 400 18 L400 0 L0 0 Z';
  return (
    <svg
      className={className}
      style={style}
      viewBox="0 0 400 200"
      preserveAspectRatio="xMinYMin meet"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <filter id={`${id}-wash`} x="-10%" y="-10%" width="120%" height="140%">
          <feTurbulence type="fractalNoise" baseFrequency="0.035" numOctaves="2" seed="7" result="noise" />
          <feDisplacementMap in="SourceGraphic" in2="noise" scale="9" result="rough" />
          <feGaussianBlur in="rough" stdDeviation="3.5" />
        </filter>
        <linearGradient id={`${id}-tint`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={palette.paleGreen} />
          <stop offset="0.55" stopColor={palette.olive} />
          <stop offset="1" stopColor={palette.ochre} />
        </linearGradient>
        <clipPath id={`${id}-land`}>
          <path d={shore} />
        </clipPath>
      </defs>

      {/* Watercolour wash bleeding a little past the line */}
      <path d={shore} fill={`url(#${id}-tint)`} opacity={0.85} filter={`url(#${id}-wash)`} />
      {/* Paper showing through the wash, near the shore */}
      <path d={shore} fill={palette.paper} opacity={0.18} />

      {/* Hachures: short strokes running down the slopes */}
      <g clipPath={`url(#${id}-land)`} stroke={palette.ink} strokeWidth={0.6} opacity={0.55}>
        {Array.from({ length: 60 }, (_, i) => {
          const x = 10 + i * 6.5;
          const top = 6 + ((i * 37) % 23);
          return <line key={i} x1={x} y1={top} x2={x + 2} y2={top + 10 + ((i * 13) % 9)} />;
        })}
      </g>

      {/* Shoreline, with a fainter offset line as on engraved charts */}
      <path d={shore} fill="none" stroke={palette.ink} strokeWidth={1.3} />
      <path
        d={shore}
        fill="none"
        stroke={palette.ink}
        strokeWidth={0.5}
        opacity={0.6}
        transform="translate(0 5)"
      />
      <path
        d={shore}
        fill="none"
        stroke={palette.ink}
        strokeWidth={0.4}
        opacity={0.35}
        transform="translate(0 10)"
      />

      {/* Reefs: sealing-wax squares with cross marks */}
      <g>
        {[
          [196, 112],
          [208, 112],
          [296, 72],
        ].map(([x = 0, y = 0]) => (
          <g key={`${x}-${y}`}>
            <rect x={x} y={y} width={11} height={11} fill={palette.reef} opacity={0.85} />
            <path
              d={`M${x + 2} ${y + 5.5} H${x + 9} M${x + 5.5} ${y + 2} V${y + 9}`}
              stroke={palette.paper}
              strokeWidth={1}
            />
          </g>
        ))}
      </g>

      {/* Soundings */}
      <g fill={palette.ink} fontFamily="'PT Serif', serif" fontSize={14}>
        <text x={60} y={160}>
          7
        </text>
        <text x={130} y={150}>
          9
        </text>
        <text x={240} y={130}>
          12
        </text>
        <text x={330} y={92}>
          15
        </text>
        <text x={180} y={182}>
          18
        </text>
      </g>
    </svg>
  );
}
